import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { ArrowRight, CheckCircle, AlertTriangle, Lightbulb, Heart, Zap, Shield } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Tips = () => {
  const { t, language } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await axios.get(`${API}/articles?category=tips&published_only=true`);
        setArticles(response.data);
      } catch (error) {
        console.error('Error fetching articles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  const quickTips = [
    {
      icon: Lightbulb,
      title: t('Avant de partir', 'Vor der Abreise'),
      tips: [
        t('Renseigne-toi sur la culture locale', 'Informiere dich über die lokale Kultur'),
        t('Apprends quelques mots de base', 'Lerne ein paar Grundwörter'),
        t('Prépare tes documents importants', 'Bereite deine wichtigen Dokumente vor')
      ],
      color: 'blue'
    },
    {
      icon: Heart,
      title: t('Gérer le stress', 'Stress bewältigen'),
      tips: [
        t('Accepte que tout ne sera pas parfait', 'Akzeptiere, dass nicht alles perfekt sein wird'),
        t('Garde contact avec tes proches', 'Bleib in Kontakt mit deinen Liebsten'),
        t('Donne-toi le temps de t\'adapter', 'Gib dir Zeit, dich anzupassen')
      ],
      color: 'red'
    },
    {
      icon: Shield,
      title: t('Erreurs à éviter', 'Fehler zu vermeiden'),
      tips: [
        t('Ne t\'isole pas, rencontre des gens', 'Isoliere dich nicht, triff Menschen'),
        t('N\'attends pas d\'être prêt à 100%', 'Warte nicht darauf, 100% bereit zu sein'),
        t('Ne compare pas constamment', 'Vergleiche nicht ständig')
      ],
      color: 'yellow'
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-200 rounded-full blur-3xl opacity-40"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-100 rounded-full blur-3xl opacity-50"></div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Content */}
            <div className="lg:col-span-7">
              <span className="text-xs font-bold tracking-widest uppercase text-blue-600 mb-4 block animate-fadeInUp">
                {t('Guide pratique', 'Praktischer Leitfaden')}
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight mb-6 animate-fadeInUp delay-100 font-outfit">
                {t('Conseils', 'Tipps')}
                <br />
                <span className="text-yellow-500">{t('pour partir', 'zum Aufbrechen')}</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8 animate-fadeInUp delay-200">
                {t(
                  "Parce que partir à l'étranger ne s'improvise pas. Voici tout ce que j'aurais aimé savoir avant de partir, pour t'aider à oser.",
                  "Weil man eine Auslandsreise nicht improvisieren kann. Hier ist alles, was ich vor meiner Abreise gerne gewusst hätte, um dir Mut zu machen."
                )}
              </p>
              <div className="flex flex-wrap gap-4 animate-fadeInUp delay-300">
                <Link to="/contact">
                  <Button 
                    size="lg"
                    className="btn-primary rounded-full px-8 py-6 text-base font-bold"
                    data-testid="tips-contact-cta"
                  >
                    {t('Une question ?', 'Eine Frage?')}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Image */}
            <div className="lg:col-span-5">
              <div className="relative animate-scaleIn">
                <img
                  src="https://images.unsplash.com/photo-1553531768-88af16561c0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwdHJhdmVsJTIwc3VpdGNhc2UlMjBhbmQlMjB0aWNrZXQlMjBmbGF0bGF5fGVufDB8fHx8MTc2ODM5MDI5N3ww&ixlib=rb-4.1.0&q=85"
                  alt="Travel preparation"
                  className="rounded-3xl shadow-2xl w-full"
                />
                <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-blue-600 rounded-2xl -z-10"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Tips Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-bold tracking-widest uppercase text-blue-600 mb-4 block">
              {t('En bref', 'Kurz gesagt')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-outfit">
              {t('Conseils rapides', 'Schnelle Tipps')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {quickTips.map((section, index) => (
              <div 
                key={index}
                className={`bg-slate-50 border border-slate-200 rounded-2xl p-8 card-hover ${
                  section.color === 'blue' ? 'hover:border-blue-300' :
                  section.color === 'red' ? 'hover:border-red-300' :
                  'hover:border-yellow-300'
                }`}
                data-testid={`quick-tips-${index}`}
              >
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 ${
                  section.color === 'blue' ? 'bg-blue-100' :
                  section.color === 'red' ? 'bg-red-100' :
                  'bg-yellow-100'
                }`}>
                  <section.icon className={`w-7 h-7 ${
                    section.color === 'blue' ? 'text-blue-600' :
                    section.color === 'red' ? 'text-red-500' :
                    'text-yellow-600'
                  }`} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-6 font-outfit">
                  {section.title}
                </h3>
                <ul className="space-y-4">
                  {section.tips.map((tip, tipIndex) => (
                    <li key={tipIndex} className="flex items-start gap-3">
                      <CheckCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                        section.color === 'blue' ? 'text-blue-600' :
                        section.color === 'red' ? 'text-red-500' :
                        'text-yellow-600'
                      }`} />
                      <span className="text-slate-600">{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Message */}
      <section className="py-20 md:py-32 bg-yellow-400">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Zap className="w-16 h-16 mx-auto mb-8 text-slate-900" />
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 font-outfit">
            {t("L'objectif est simple", 'Das Ziel ist einfach')}
          </h2>
          <p className="text-xl text-slate-800 leading-relaxed">
            {t(
              "Te rassurer, t'aider et te donner confiance pour oser. Tu n'as pas besoin d'être prêt à 100% pour commencer.",
              "Dich beruhigen, dir helfen und dir Vertrauen geben, um es zu wagen. Du musst nicht 100% bereit sein, um anzufangen."
            )}
          </p>
        </div>
      </section>

      {/* Articles Section */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-bold tracking-widest uppercase text-blue-600 mb-4 block">
              {t('Articles détaillés', 'Detaillierte Artikel')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-outfit">
              {t('Guides complets', 'Vollständige Leitfäden')}
            </h2>
          </div>

          {loading ? (
            <div className="flex justify-center">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : articles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.map((article, index) => (
                <Link 
                  key={article.id} 
                  to={`/blog/${article.slug}`}
                  className="group"
                  data-testid={`tips-article-${index}`}
                >
                  <article className="bg-white border border-slate-200 rounded-2xl overflow-hidden card-hover h-full">
                    {article.image_url && (
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={article.image_url}
                          alt={language === 'fr' ? article.title_fr : (article.title_de || article.title_fr)}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="text-lg font-bold text-slate-900 mb-3 font-outfit group-hover:text-blue-600 transition-colors">
                        {language === 'fr' ? article.title_fr : (article.title_de || article.title_fr)}
                      </h3>
                      <p className="text-slate-600 text-sm line-clamp-2">
                        {language === 'fr' ? article.excerpt_fr : (article.excerpt_de || article.excerpt_fr)}
                      </p>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
              <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
              <p className="text-slate-600 text-lg mb-6">
                {t(
                  "Les guides détaillés arrivent bientôt ! En attendant, explore le blog.",
                  "Detaillierte Leitfäden kommen bald! In der Zwischenzeit erkunde den Blog."
                )}
              </p>
              <Link to="/blog">
                <Button className="btn-primary rounded-full" data-testid="tips-empty-cta">
                  {t('Explorer le blog', 'Blog erkunden')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default Tips;
